import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDevice, useSensorData, useMTCommand } from '@/hooks';
import { commandService } from '@/api/services';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { Modal } from '@/components/common/Modal/Modal';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { resolveSecurityMode, resolveOnlineState, onlineStateVariant, isIntrusionZone, getZoneDisplayName } from '@/lib/enum-resolvers';
import { useProductCapabilities } from '@/hooks';
import { getAutoConfigLabel, isBuiltInZone } from '@/lib/product-capabilities';
import { findPendingMT } from '@/lib/sensor-commands';

// Numeric security mode constants matching the API
const SECURITY_DISARMED = 0;
const SECURITY_ONBOARD = 1;
const SECURITY_AWAY = 2;

const ALL_MODE_OPTIONS = [SECURITY_DISARMED, SECURITY_ONBOARD, SECURITY_AWAY] as const;

const MODE_CONFIG: Record<number, { color: string; bgClass: string; ringClass: string }> = {
  [SECURITY_DISARMED]: {
    color: 'green',
    bgClass: 'bg-green-500',
    ringClass: 'ring-green-500/30',
  },
  [SECURITY_ONBOARD]: {
    color: 'teal',
    bgClass: 'bg-teal-500',
    ringClass: 'ring-teal-500/30',
  },
  [SECURITY_AWAY]: {
    color: 'blue',
    bgClass: 'bg-blue-500',
    ringClass: 'ring-blue-500/30',
  },
};

const MODE_COMMANDS: Record<number, NAMTMessageType> = {
  [SECURITY_DISARMED]: NAMTMessageType.SET_SECURITY_DISARMED,
  [SECURITY_ONBOARD]: NAMTMessageType.SET_SECURITY_ONBOARD,
  [SECURITY_AWAY]: NAMTMessageType.SET_SECURITY_AWAY,
};

export function SecurityStatusPanel() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();
  const { sensorData, availability } = useSensorData();

  const caps = useProductCapabilities();

  const [confirmMode, setConfirmMode] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [pendingMode, setPendingMode] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { sendCommand, mtObjList } = useMTCommand(deviceUUID ?? '');
  // Keep the button spinning until the device actually receives the command
  // (or the pending window times out), not just until the enqueue POST
  // returns — the REST call resolves as soon as it's queued, well before the
  // device checks in.
  const refreshPending = refreshing || !!findPendingMT(mtObjList, NAMTMessageType.GET_SECURITY_DATA);

  const handleRefresh = async () => {
    if (!deviceUUID) return;
    setRefreshing(true);
    try {
      await sendCommand(NAMTMessageType.GET_SECURITY_DATA);
    } finally {
      setRefreshing(false);
    }
  };

  // No-payload command -- header-only on the wire (see NAMTObject.cs's
  // buildMTSettingsPayload(), which has no case for this type and falls
  // through to the default header-only path). Matches mobile's Arm/Disarm
  // popup exactly: "Clear Active Alerts" button, no confirmation dialog,
  // fire-and-forget (mobile doesn't gate on success either).
  const [clearingAlarms, setClearingAlarms] = useState(false);
  const clearAlarmsPending = clearingAlarms || !!findPendingMT(mtObjList, NAMTMessageType.MT_MESSAGE_CLEAR_ALARMS);

  const handleClearAlarms = async () => {
    if (!deviceUUID) return;
    setClearingAlarms(true);
    try {
      await sendCommand(NAMTMessageType.MT_MESSAGE_CLEAR_ALARMS);
    } finally {
      setClearingAlarms(false);
    }
  };

  // Filter mode options based on device capabilities
  const modeOptions = caps.supportsOnboardMode
    ? ALL_MODE_OPTIONS
    : ALL_MODE_OPTIONS.filter((m) => m !== SECURITY_ONBOARD);

  const rawMode = selectedDeviceInfo?.globalSettings?.securityOperatingMode;
  const currentMode = typeof rawMode === 'number' ? rawMode : SECURITY_DISARMED;
  const modeConfig = MODE_CONFIG[currentMode] ?? MODE_CONFIG[SECURITY_DISARMED];

  const allZones = useMemo(() => sensorData?.securitySensorlist ?? [], [sensorData]);
  // Only show user-configurable zones, not system zones (tamper, inactivity, low batt, disarm/arm control)
  const zones = useMemo(() => allZones.filter((z) => isIntrusionZone(z)), [allZones]);

  const zoneSummary = useMemo(() => {
    const total = zones.length;
    const alarmed = zones.filter((z) => z.alarmPresent).length;
    const bypassed = zones.filter((z) => z.isBypassed).length;
    const online = zones.filter((z) => resolveOnlineState(z.onlineState) === 'Online').length;
    return { total, alarmed, bypassed, online };
  }, [zones]);

  async function handleConfirm() {
    if (confirmMode == null || !deviceUUID) return;
    setSending(true);
    try {
      await commandService.sendOnDemandCommand({
        deviceUUID,
        mtMessageType: MODE_COMMANDS[confirmMode],
      });
      setPendingMode(confirmMode);
    } catch {
      // command failed silently — polling will show actual state
    } finally {
      setSending(false);
      setConfirmMode(null);
    }
  }

  if (deviceInfoLoading && !selectedDeviceInfo) {
    return <PageSpinner />;
  }

  if (!selectedDeviceInfo) {
    return (
      <EmptyState
        title="No Device Selected"
        description="Select a device to view security status."
      />
    );
  }

  const displayMode = pendingMode ?? currentMode;

  // Clear pending when server catches up
  if (pendingMode != null && currentMode === pendingMode) {
    // Will clear on next render cycle
    setTimeout(() => setPendingMode(null), 0);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Security Status</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            View and control your vessel's security system
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          On-Demand
        </Button>
      </div>

      {/* Current Mode Indicator */}
      <div className="card rounded-xl border border-slate-200 bg-white p-8 dark:border-navy-700 dark:bg-navy-900">
        <div className="flex flex-col items-center gap-6">
          <div className="relative">
            <div
              className={`h-28 w-28 rounded-full ${modeConfig.bgClass} ring-8 ${modeConfig.ringClass} flex items-center justify-center`}
            >
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
          </div>

          <div className="text-center">
            {pendingMode != null ? (
              <>
                <p className="text-lg font-semibold text-yellow-500">Command Pending</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Switching to {resolveSecurityMode(pendingMode)}...
                </p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {resolveSecurityMode(displayMode)}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Current Security Mode</p>
              </>
            )}
          </div>

          {/* Mode buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {modeOptions.map(
              (mode) => (
                <Button
                  key={mode}
                  variant={currentMode === mode ? 'primary' : 'secondary'}
                  disabled={currentMode === mode || pendingMode != null}
                  onClick={() => setConfirmMode(mode)}
                >
                  {resolveSecurityMode(mode)}
                </Button>
              ),
            )}
          </div>

          {/* Clear Active Alerts -- matches mobile's Arm/Disarm popup: same
              label, no confirmation dialog, fire-and-forget. */}
          <Button variant="secondary" onClick={handleClearAlarms} loading={clearAlarmsPending}>
            Clear Active Alerts
          </Button>
        </div>
      </div>

      {/* Security Zones Summary */}
      {availability.hasSecurity && (
        <div className="card rounded-xl border border-slate-200 bg-white p-6 dark:border-navy-700 dark:bg-navy-900">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Security Zones</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg bg-slate-50 p-4 text-center dark:bg-navy-800">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{zoneSummary.total}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Zones</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-4 text-center dark:bg-navy-800">
              <p className="text-2xl font-bold text-green-500">{zoneSummary.online}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Online</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-4 text-center dark:bg-navy-800">
              <p className="text-2xl font-bold text-red-500">{zoneSummary.alarmed}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">In Alarm</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-4 text-center dark:bg-navy-800">
              <p className="text-2xl font-bold text-yellow-500">{zoneSummary.bypassed}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Bypassed</p>
            </div>
          </div>

          {/* Zone list */}
          <div className="mt-4 space-y-2">
            {zones.map((zone, idx) => (
              <div
                key={`${zone.zoneNum}-${idx}`}
                className={`flex items-center justify-between rounded-lg border p-3 ${
                  zone.alarmPresent
                    ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                    : zone.isBypassed
                      ? 'border-yellow-300 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
                      : 'border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {getAutoConfigLabel(caps, zone) || getZoneDisplayName(zone)}
                  </span>
                  {zone.alarmPresent && (
                    <StatusBadge label="ALARM" variant="danger" dot />
                  )}
                  {zone.isBypassed && (
                    <StatusBadge label="Bypassed" variant="warning" dot />
                  )}
                </div>
                {/* Don't show Disconnected for built-in zones */}
                {isBuiltInZone(caps, zone) && resolveOnlineState(zone.onlineState) === 'Disconnected' ? null : (
                  <StatusBadge
                    label={resolveOnlineState(zone.onlineState)}
                    variant={onlineStateVariant(zone.onlineState)}
                    dot
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmMode != null}
        onClose={() => setConfirmMode(null)}
        title="Confirm Security Mode Change"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Are you sure you want to change the security mode to{' '}
            <span className="font-semibold">{confirmMode != null && resolveSecurityMode(confirmMode)}</span>?
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            This command will be sent to your device via satellite. It may take a few minutes to take effect.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setConfirmMode(null)} disabled={sending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} loading={sending}>
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
